<template>
  <!-- The customer's gate PIN as a password field: the eye reveals it (and
       records that in the booking's history), and it hides again after a
       minute. Read-only: Actions issues a new one. -->
  <BaseInputField
    label="Gate PIN"
    :disabled="!hasPin"
    :hint="
      hasPin
        ? 'Revealing the PIN is recorded with your name.'
        : 'This customer has no PIN yet.'
    "
  >
    <template #default="{ inputProps }">
      <div class="relative">
        <input
          v-bind="inputProps"
          :value="pin ?? (hasPin ? '000000' : '')"
          :type="pin ? 'text' : 'password'"
          readonly
          class="border-background-fg/20 bg-background focus:border-primary focus:ring-primary relative w-full rounded-md border px-3 py-2 pr-10 text-left text-sm tabular-nums shadow-sm focus:outline-none focus:ring-1"
        />
        <div class="absolute inset-y-0 right-0 flex items-center pr-3">
          <button
            type="button"
            :disabled="!hasPin || busy"
            :aria-label="pin ? 'Hide PIN' : 'Reveal PIN'"
            @click="pin ? hide() : reveal()"
          >
            <IconHeroiconsOutlineEye v-if="pin" />
            <IconHeroiconsOutlineEyeOff v-else />
          </button>
        </div>
      </div>
    </template>
  </BaseInputField>
</template>

<script setup lang="ts">
import { ApiError } from "@/api";
import { revealStoragePin } from "@/services/storage/revealStoragePin";

const props = defineProps<{ bookingId: string; hasPin: boolean }>();

// Held here only, never in the (persisted) query cache.
const pin = ref<string | null>(null);
const busy = ref(false);

const { start: startHideTimer, stop: stopHideTimer } = useTimeoutFn(
  () => hide(),
  60 * 1000,
  { immediate: false },
);

const hide = () => {
  pin.value = null;
  stopHideTimer();
};

const reveal = async () => {
  busy.value = true;
  try {
    pin.value = await revealStoragePin(props.bookingId);
    startHideTimer();
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    toast.error(error.detail ?? "The PIN could not be shown. Try again.");
  } finally {
    busy.value = false;
  }
};

onBeforeUnmount(hide);
</script>
